import { useId, useLayoutEffect, useRef, useState } from 'react'
import { scaleLinear } from 'd3-scale'
import { monotonePath } from '../../../platform/charts/monotonePath'
import { InlineLink } from '../../../platform/primitives/InlineLink'
import { Money } from '../../../platform/primitives/Money'
import { SegmentedControl } from '../../../platform/primitives/SegmentedControl'
import { usePanelHit } from '../../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { MONTH_LONG, MONTH_SHORT } from '../../cashflow/cashflowPeriod'
import type { ChartView, MonthSlot } from './chartView'

// A 100×100 viewBox: a coordinate is also its percentage, so the HTML marker needs no conversion.
const W = 100
const H = 100
const PAD_X = 1.25
const PAD_Y = 12.73

type Pt = readonly [number, number]

/** Runs of consecutive months that have a point: a missing month breaks the line rather than inventing a value. */
function runs(points: readonly (Pt | null)[]): Pt[][] {
  const out: Pt[][] = []
  let run: Pt[] = []
  for (const p of points) {
    if (p === null) {
      if (run.length > 0) out.push(run)
      run = []
    } else run.push(p)
  }
  if (run.length > 0) out.push(run)
  return out
}

/** Runs of two or more months are a stroke; the SVG is stretched, so a lone month is drawn separately as an HTML dot. */
function Line({ tone, groups }: { tone: 'now' | 'then'; groups: Pt[][] }) {
  return (
    <>
      {groups.map((g) =>
        g.length < 2 ? null : (
          <path key={`${g[0]?.[0]}`} className={`ov-lines__line ov-lines__line--${tone}`} d={monotonePath(g)} fill="none" vectorEffect="non-scaling-stroke" data-chart />
        ),
      )}
    </>
  )
}

function Dots({ tone, groups }: { tone: 'now' | 'then'; groups: Pt[][] }) {
  return (
    <>
      {groups.map((g) => {
        const p = g[0]
        return g.length === 1 && p !== undefined ? (
          <span
            key={p[0]}
            className={`ov-lines__dot ov-lines__dot--${tone}`}
            data-chart
            style={{ '--x': `${p[0]}%`, '--y': `${p[1]}%` } as React.CSSProperties}
          />
        ) : null
      })}
    </>
  )
}

/** Four 44px pills and two pagers do not fit a phone row; three do. Decided by `useBreakpoint`, never the viewport width. */
const PAGE_DESKTOP = 4
const PAGE_PHONE = 3

/**
 * Two smooth lines, month by month: this year solid, last year dashed, over a zero line.
 * Every plotted value is a server bucket's `net` (`Period.netCents`); the only arithmetic here is
 * the pixel scale. A month with no bucket has no point. The current month wears a marker and, being
 * partial, is announced as "so far". Presentational: no fetching, no money maths.
 */
export function CashflowLines({ view, today, labelledBy, hrefFor }: { view: ChartView; today: string; labelledBy: string; hrefFor: (month: number) => string }) {
  const summaryId = useId()
  const { thisYear, lastYear, year, currency } = view
  const current = Number(today.slice(5, 7)) - 1
  const nets = [...thisYear, ...lastYear].flatMap((s) => (s === null ? [] : [s.netCents]))
  const lo = Math.min(0, ...nets)
  const hi = Math.max(0, ...nets)
  const y = scaleLinear()
    .domain(lo === hi ? [-1, 1] : [lo, hi])
    .range([H - PAD_Y, PAD_Y])
  const x = scaleLinear().domain([0, 11]).range([PAD_X, W - PAD_X])
  const at = (s: MonthSlot, i: number): Pt | null => (s === null ? null : [x(i), y(s.netCents)])
  const mine = thisYear.map(at)
  const prior = lastYear.map(at)
  const [selected, setSelected] = useState(current)
  const PAGE = useBreakpoint() === 'phone' ? PAGE_PHONE : PAGE_DESKTOP
  // The page is kept as the month it starts at, so a resize across the breakpoint re-pages around it instead of stranding it.
  const [anchor, setAnchor] = useState(current)
  const page = Math.floor(anchor / PAGE)
  const setPage = (p: number) => setAnchor(p * PAGE)
  const refocus = useRef(false)
  const row = useRef<HTMLDivElement>(null)
  const hit = usePanelHit()
  const pagerStyle = { minHeight: hit.minHeight, minWidth: hit.minHeight }
  useLayoutEffect(() => {
    if (!refocus.current) return
    refocus.current = false
    row.current?.querySelector<HTMLInputElement>(`input[value="${selected}"]`)?.focus()
  }, [selected, page])
  const here = mine[selected] ?? null
  const now = thisYear[selected] ?? null
  const label = MONTH_LONG[selected] ?? ''
  const first = page * PAGE
  const last = first + PAGE >= MONTH_LONG.length
  /** The radios step within the page on their own. Only a step that would leave it (or the selectable range) is taken here. */
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    if (!(e.target instanceof HTMLInputElement) || e.target.value !== String(selected)) return
    const j = selected + (e.key === 'ArrowLeft' ? -1 : 1)
    if (j >= first && j < first + PAGE && j <= current) return
    e.preventDefault()
    if (j < 0 || j > current) return
    refocus.current = true
    setSelected(j)
    setAnchor(j)
  }

  return (
    <div className="ov-lines">
      <div className="ov-lines__top">
        <InlineLink to={hrefFor(selected)} className="ov-chip ov-lines__chip" aria-label={`Open cashflow for ${label} ${year}`}>
          <span>
            {MONTH_SHORT[selected]}
            {now === null ? ' · no data' : <>{now.complete ? '' : ' so far'} · net <Money cents={now.netCents} currency={currency} signed /></>}
          </span>
        </InlineLink>
      </div>
      <div className="ov-lines__plot">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-labelledby={labelledBy}
          aria-describedby={summaryId}
          className="ov-lines__svg"
        >
          <line className="ov-lines__zero" x1={0} x2={W} y1={y(0)} y2={y(0)} vectorEffect="non-scaling-stroke" />
          <Line tone="then" groups={runs(prior)} />
          <Line tone="now" groups={runs(mine)} />
        </svg>
        <Dots tone="then" groups={runs(prior)} />
        <Dots tone="now" groups={runs(mine)} />
        {here !== null && (
          <span
            className="ov-lines__marker"
            data-chart
            data-testid="cashflow-marker"
            data-month={String(selected + 1)}
            style={{ '--x': `${here[0]}%`, '--y': `${here[1]}%` } as React.CSSProperties}
          />
        )}
      </div>
      <div className="ov-pills">
        <button
          type="button"
          className={`ov-pills__page ${hit.className}${page === 0 ? ' ov-pills__page--end' : ''}`}
          style={pagerStyle}
          aria-label="Earlier months"
          aria-disabled={page === 0 || undefined}
          onClick={() => page > 0 && setPage(page - 1)}
        >
          <span>«</span>
        </button>
        <div ref={row} onKeyDown={onKey}>
          <SegmentedControl
            label="Month"
            name="overview-cashflow-month"
            size="panel"
            tone="outline"
            value={String(selected)}
            onChange={(v) => setSelected(Number(v))}
            options={MONTH_SHORT.slice(first, first + PAGE).map((m, k) => {
              const i = first + k
              return i > current ? { value: String(i), label: m, unavailable: 'Not yet' } : { value: String(i), label: m }
            })}
          />
        </div>
        <button
          type="button"
          className={`ov-pills__page ${hit.className}${last ? ' ov-pills__page--end' : ''}`}
          style={pagerStyle}
          aria-label="Later months"
          aria-disabled={last || undefined}
          onClick={() => !last && setPage(page + 1)}
        >
          <span>»</span>
        </button>
      </div>
      <p id={summaryId} className="sr-only">
        Net cashflow by month, {year} against {year - 1}. {now === null ? '' : `${label} ${now.complete ? '' : 'so far '}is the current month.`}
      </p>
      <table className="sr-only">
        <caption>Net cashflow by month</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">{year}</th>
            <th scope="col">{year - 1}</th>
          </tr>
        </thead>
        <tbody>
          {MONTH_LONG.map((m, i) => {
            const a = thisYear[i] ?? null
            const b = lastYear[i] ?? null
            if (a === null && b === null) return null
            return (
              <tr key={m}>
                <th scope="row">{m}</th>
                <td>{a === null ? 'No data' : <Money cents={a.netCents} currency={currency} signed />}</td>
                <td>{b === null ? 'No data' : <Money cents={b.netCents} currency={currency} signed />}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
